import React, { useState } from 'react';
import { content, logoUrl } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { Plane, Globe } from 'lucide-react';

const SplashScreen = () => {
  const { selectLanguage } = useLanguage();
  const [hoveredLang, setHoveredLang] = useState(null);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        {/* Animated circles */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
        
        {/* Floating planes animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <Plane className="absolute w-8 h-8 text-blue-400/20 animate-float-plane-1" style={{ top: '20%', left: '-10%' }} />
          <Plane className="absolute w-6 h-6 text-cyan-400/20 animate-float-plane-2" style={{ top: '60%', left: '-5%' }} />
          <Plane className="absolute w-10 h-10 text-orange-400/20 animate-float-plane-3" style={{ top: '40%', left: '-15%' }} />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4">
        {/* Logo */}
        <div className="mb-8 animate-fade-in-up">
          <div className="relative">
            <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-2xl scale-150" />
            <div className="relative w-48 h-48 md:w-56 md:h-56 bg-white rounded-2xl p-4 shadow-2xl">
              <img 
                src={logoUrl} 
                alt="TransAfrica Canada" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* Welcome text */}
        <div className="text-center mb-12 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <p className="text-blue-300 text-lg md:text-xl mb-2 tracking-widest uppercase font-heading">
            Bienvenue / Welcome
          </p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 font-heading tracking-wide">
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-orange-400 bg-clip-text text-transparent">
              TRANSAFRICA
            </span>
            <br />
            <span className="text-white">CANADA</span>
          </h1>
          <p className="text-slate-400 text-base md:text-lg max-w-md mx-auto font-sans">
            Votre pont logistique / Your logistics bridge
          </p>
        </div>

        {/* Language selection */}
        <div className="animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <div className="flex items-center gap-2 mb-6">
            <Globe className="w-5 h-5 text-slate-400" />
            <p className="text-slate-400 text-sm uppercase tracking-wider">
              Choisissez votre langue / Choose your language
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            {/* French button */}
            <button
              onClick={() => selectLanguage('fr')}
              onMouseEnter={() => setHoveredLang('fr')}
              onMouseLeave={() => setHoveredLang(null)}
              className="group relative px-12 py-4 rounded-xl overflow-hidden transition-all duration-300 hover:scale-105"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 transition-all duration-300 group-hover:from-blue-500 group-hover:to-blue-600" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_70%)]" />
              </div>
              <span className="relative flex items-center gap-3 text-white font-semibold text-lg">
                <span className="text-2xl">🇫🇷</span>
                Français
              </span>
            </button>

            {/* Divider */}
            <div className="hidden sm:flex items-center">
              <div className="w-px h-12 bg-slate-600" />
            </div>

            {/* English button */}
            <button
              onClick={() => selectLanguage('en')}
              onMouseEnter={() => setHoveredLang('en')}
              onMouseLeave={() => setHoveredLang(null)}
              className="group relative px-12 py-4 rounded-xl overflow-hidden transition-all duration-300 hover:scale-105"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-cyan-700 transition-all duration-300 group-hover:from-cyan-500 group-hover:to-cyan-600" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_70%)]" />
              </div>
              <span className="relative flex items-center gap-3 text-white font-semibold text-lg">
                <span className="text-2xl">🇬🇧</span>
                English
              </span>
            </button>
          </div>
        </div>

        {/* Bottom tagline */}
        <div className="absolute bottom-8 left-0 right-0 text-center animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <p className="text-slate-500 text-sm">
            Canada • Sénégal • Cameroun • RD Congo • Congo
          </p>
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
